import React from 'react';
import { Field, FieldArray, formValueSelector, reduxForm } from 'redux-form';
import i18n from 'i18next';
import { Paper, TextField } from 'material-ui';
import RaisedButton from 'material-ui/RaisedButton';
import PropTypes from 'prop-types';
import {
    renderCheckbox,
    renderNumberField,
    renderSelectField,
    renderTextField,
} from '../../../services/form';
import styles from '../../../styles/styles';
import style from './QuizForm.css';
import withTranslation from '../../../hoc/withTranslation';
import { blue200, red100, red400 } from 'material-ui/styles/colors';
import { connect } from 'react-redux';
import MenuItem from 'material-ui/MenuItem';

const validate = values => {
    const errors = {};
    ['name'].forEach(field => {
        if (!values[field]) {
            errors[field] = i18n.t('generic.required');
        }
    });
    if (values.geolocation_type === 'gps') {
        ['latitude', 'longitude'].forEach(field => {
            const value = values[field];
            if ((
                isNaN(Number.parseFloat(value))
            ) && errors[field] === undefined) {
                errors[field] = i18n.t('generic.value_must_be_int');
            }
        });
    }
    if (!values.quiz_questions || !values.quiz_questions.length) {
        errors.quiz_questions = { _error: i18n.t('module.quiz.question.no_question_error') };
    } else {
        let questionsErrors = [];
        let questionsDisplayOrders = [];
        let countRemovedQuestion = 0;
        values.quiz_questions.forEach((question, questionIndex) => {
            let questionErrors = {};
            const isQuestionRemoved = question.remove !== undefined && question.remove === true;
            if (isQuestionRemoved) {
                countRemovedQuestion++;
            } else {
                if (!question.title) {
                    questionErrors.title = i18n.t('generic.required');
                    questionsErrors[questionIndex] = questionErrors;
                }
                if (question.display_order === null || isNaN(Number(question.display_order))) {
                    questionErrors.display_order = i18n.t('generic.value_must_be_int');
                    questionsErrors[questionIndex] = questionErrors;
                } else if (question.display_order < 0) {
                    questionErrors.display_order = i18n.t('generic.value_must_be_positive_int');
                    questionsErrors[questionIndex] = questionErrors;
                } else {
                    questionsDisplayOrders.push(Number(question.display_order));
                }

                if (!question.quiz_responses || !question.quiz_responses.length) {
                    questionErrors.quiz_responses = {
                        _error: i18n.t('module.quiz.response.no_response_error'),
                    };
                    questionsErrors[questionIndex] = questionErrors;
                } else {
                    let responsesErrors = [];
                    let responsesDisplayOrders = [];
                    let countValidResponse = 0;
                    let countRemovedResponse = 0;
                    question.quiz_responses.forEach((response, responseIndex) => {
                        let responseErrors = {};
                        const isResponseRemoved = response.remove
                            !== undefined
                            && response.remove
                            === true;
                        if (isResponseRemoved) {
                            countRemovedResponse++;
                        } else {
                            if (!response.label) {
                                responseErrors.label = i18n.t('generic.required');
                                responsesErrors[responseIndex] = responseErrors;
                            }
                            if (response.display_order
                                === null
                                || isNaN(Number(response.display_order))) {
                                responseErrors.display_order = i18n.t('generic.value_must_be_int');
                                responsesErrors[responseIndex] = responseErrors;
                            } else if (response.display_order < 0) {
                                responseErrors.display_order = i18n.t(
                                    'generic.value_must_be_positive_int');
                                responsesErrors[responseIndex] = responseErrors;
                            } else {
                                responsesDisplayOrders.push(Number(response.display_order));
                            }
                            if (response.point === null || isNaN(Number(response.point))) {
                                responseErrors.point = i18n.t('generic.value_must_be_int');
                                responsesErrors[responseIndex] = responseErrors;
                            } else if (response.point < 0) {
                                responseErrors.point = i18n.t('generic.value_must_be_positive_int');
                                responsesErrors[responseIndex] = responseErrors;
                            }
                            if (response.is_valid) {
                                countValidResponse++;
                            }
                        }
                    });
                    if (countRemovedResponse === question.quiz_responses.length) {
                        questionErrors.quiz_responses = {
                            _error: i18n.t('module.quiz.response.no_response_error'),
                        };
                        questionsErrors[questionIndex] = questionErrors;
                    } else {
                        if ((
                            new Set(responsesDisplayOrders)
                        ).size !== responsesDisplayOrders.length) {
                            responsesErrors._error = i18n.t(
                                'module.quiz.response.duplicate_order_error');
                            questionErrors.quiz_responses = responsesErrors;
                            questionsErrors[questionIndex] = questionErrors;
                        }
                        if (countValidResponse === 0) {
                            responsesErrors._error = i18n.t(
                                'module.quiz.question.question_min_answer');
                            questionErrors.quiz_responses = responsesErrors;
                            questionsErrors[questionIndex] = questionErrors;
                        } else if (countValidResponse > 1 && question.type === 'RADIO') {
                            responsesErrors._error = i18n.t('module.quiz.question.radio_max_answer');
                            questionErrors.quiz_responses = responsesErrors;
                            questionsErrors[questionIndex] = questionErrors;
                        }

                        if (responsesErrors.length > 0) {
                            questionErrors.quiz_responses = responsesErrors;
                            questionsErrors[questionIndex] = questionErrors;
                        }
                    }
                }
            }
        });

        if (countRemovedQuestion === values.quiz_questions.length) {
            errors.quiz_questions = { _error: i18n.t('module.quiz.question.no_question_error') };
        } else {
            if ((
                new Set(questionsDisplayOrders)
            ).size !== questionsDisplayOrders.length) {
                questionsErrors._error = i18n.t('module.quiz.question.duplicate_order_error');
                errors.quiz_questions = questionsErrors;
            }
            if (questionsErrors.length > 0) {
                errors.quiz_questions = questionsErrors;
            }
        }
    }
    return errors;
};

export class QuizForm extends React.Component {

    static propTypes = {
        onAddressChanged: PropTypes.func,
        address: PropTypes.string,
        latlng: PropTypes.object,
        onLatChanged: PropTypes.func,
        onLngChanged: PropTypes.func,
        onFindAddress: PropTypes.func,
        onGeolocationTypeChange: PropTypes.func,
        handleSubmit: PropTypes.func,
        onFormReset: PropTypes.func,
        quiz: PropTypes.object,
        t: PropTypes.func,
        quizQuestions: PropTypes.array,
        geolocationType: PropTypes.string,
        defaultGeolocationType: PropTypes.string,
    };

    componentWillReceiveProps(nextProps) {
        if (nextProps.latlng && this.props.latlng !== nextProps.latlng) {
            const newLat = (
                typeof nextProps.latlng.lat === 'string'
            )
                ? Number.parseFloat(nextProps.latlng.lat)
                : nextProps.latlng.lat;
            const newLng = (
                typeof nextProps.latlng.lng === 'string'
            )
                ? Number.parseFloat(nextProps.latlng.lng)
                : nextProps.latlng.lng;
            this.props.change('latitude', newLat);
            this.props.change('longitude', newLng);
        }
    }

    onLatChanged = (event) => {
        const value = event.target.value;
        if (this.props.onLatChanged && value && !isNaN(Number.parseFloat(value))) {
            this.props.onLatChanged(Number.parseFloat(value));
        }
    };

    onLngChanged = (event) => {
        const value = event.target.value;
        const newLng = Number.parseFloat(value);
        if (this.props.onLngChanged && value && !isNaN(newLng)) {
            this.props.onLngChanged(newLng);
        }
    };

    onAddressChanged = event => {
        const value = event.target.value;

        if (this.props.onAddressChanged) {
            this.props.onAddressChanged(value);
        }
    };

    onFindAddress = () => {
        const value = this.props.address;
        if (this.props.onFindAddress && value) {
            this.props.onFindAddress(value);
        }
    };

    renderQuizResponse = ({
        fields,
        meta: {
            error,
            submitFailed,
        },
    }) => (
        <div>
            <div className="row">
                {submitFailed && error && <div className="col-xs-12">
                    <p
                        style={{
                            color: red400,
                            marginLeft: '15px',
                        }}
                    >{error}</p>
                </div>}
                {fields.map((member, index) => {
                    const regex = /[-+]?\d+(\.\d+)?/g;
                    const indexes = member.match(regex);
                    let response = null;
                    let question = null;
                    if (indexes && indexes.length > 0) {
                        if (this.props.quizQuestions
                            && this.props.quizQuestions.length > 0
                            && this.props.quizQuestions[indexes[0]] !== null) {
                            question = this.props.quizQuestions[indexes[0]];
                        }
                        if (question) {

                            if (question.quiz_responses
                                && question.quiz_responses.length > 0
                                && question.quiz_responses[indexes[1]] !== null) {
                                response = question.quiz_responses[indexes[1]];
                            }
                        }
                    }
                    const questionRemoved = question
                        && question.remove !== undefined
                        && question.remove === true;
                    const responseRemoved = response
                        && response.remove !== undefined
                        && response.remove === true;
                    return (
                        <Paper
                            key={index}
                            style={style.responseContainer}
                            className="col-xs-12 col-sm-5 col-md-5"
                        >
                            <div
                                className="row"
                                style={{ width: '100%' }}
                            >
                                <div className="col-xs-12">
                                    <Field
                                        name={`${member}.label`}
                                        type="text"
                                        component={renderTextField}
                                        label={this.props.t('module.quiz.response.label')}
                                        fullWidth={true}
                                        disabled={questionRemoved || responseRemoved}
                                    />
                                </div>
                                <div className="col-xs-12">
                                    <Field
                                        name={`${member}.point`}
                                        component={renderNumberField}
                                        label={this.props.t('module.quiz.response.point')}
                                        type="number"
                                        fullWidth={true}
                                        disabled={questionRemoved || responseRemoved}
                                        parse={value => Number(value)}
                                    />
                                </div>
                                <div className="col-xs-12">
                                    <Field
                                        name={`${member}.display_order`}
                                        component={renderNumberField}
                                        label={this.props.t('module.quiz.response.display_order')}
                                        type="number"
                                        fullWidth={true}
                                        disabled={questionRemoved || responseRemoved}
                                        parse={value => Number(value)}
                                    />
                                </div>
                                <div className="col-xs-12">
                                    <Field
                                        id={`${member}.is_valid`}
                                        name={`${member}.is_valid`}
                                        component={renderCheckbox}
                                        label={this.props.t('module.quiz.response.is_valid')}
                                        style={{ marginTop: '15px' }}
                                        disabled={questionRemoved || responseRemoved}
                                    />
                                </div>
                                {response && response.id !== undefined
                                    && (
                                        <div className="col-xs-12">
                                            <Field
                                                id={`${member}.remove`}
                                                name={`${member}.remove`}
                                                component={renderCheckbox}
                                                label={this.props.t('generic.delete')}
                                                style={{ marginTop: '15px' }}
                                                disabled={questionRemoved}
                                            />
                                        </div>
                                    )}
                                {response && response.key !== undefined
                                    && (
                                        <RaisedButton
                                            style={{
                                                ...styles.formButton,
                                                margin: '15px',
                                            }}
                                            label={this.props.t('generic.delete')}
                                            type="button"
                                            primary={false}
                                            backgroundColor={red100}
                                            onClick={() => fields.remove(index)}
                                        />
                                    )}
                            </div>
                        </Paper>
                    );
                })}
            </div>

            <RaisedButton
                style={styles.formButton}
                label={this.props.t('module.quiz.add_response')}
                type="button"
                onClick={() => fields.push({
                    label: '',
                    is_valid: false,
                    display_order: fields.length,
                    point: 0,
                    key: 'new_' + Math.random().toString(16).substring(2, 8),
                })}
                backgroundColor={blue200}
            />
        </div>
    );

    renderQuizQuestion = ({
        fields,
        meta: {
            error,
            submitFailed,
        },
    }) => (
        <div>
            {submitFailed
                && error
                && (
                    <p style={{ color: red400 }}>{error}</p>
                )
            }
            {fields.map((member, index) => {
                let question = null;
                if (this.props.quizQuestions
                    && this.props.quizQuestions.length > 0
                    && this.props.quizQuestions[index] !== null) {
                    question = this.props.quizQuestions[index];
                }
                const questionRemoved = question
                    && question.remove !== undefined
                    && question.remove === true;
                return (
                    <Paper
                        key={index}
                        style={style.questionContainer}
                    >
                        <Field
                            name={`${member}.title`}
                            type="text"
                            component={renderTextField}
                            label={this.props.t('module.quiz.question.title')}
                            fullWidth={true}
                            disabled={questionRemoved}
                        />
                        <Field
                            name={`${member}.hint`}
                            type="text"
                            component={renderTextField}
                            label={this.props.t('module.quiz.question.hint')}
                            fullWidth={true}
                            disabled={questionRemoved}
                        />
                        <Field
                            name={`${member}.information`}
                            type="text"
                            component={renderTextField}
                            label={this.props.t('module.quiz.question.information')}
                            fullWidth={true}
                            disabled={questionRemoved}
                        />
                        <Field
                            name={`${member}.display_order`}
                            component={renderNumberField}
                            label={this.props.t('module.quiz.question.display_order')}
                            type="number"
                            fullWidth={true}
                            parse={value => Number(value)}
                            disabled={questionRemoved}
                        />
                        {question && question.id !== undefined
                            && (
                                <Field
                                    id={`${member}.remove`}
                                    name={`${member}.remove`}
                                    component={renderCheckbox}
                                    label={this.props.t('generic.delete')}
                                />
                            )}
                        {question && question.key !== undefined
                            && (
                                <RaisedButton
                                    style={styles.formButton}
                                    label={this.props.t('generic.delete')}
                                    type="button"
                                    primary={false}
                                    backgroundColor={red100}
                                    onClick={() => fields.remove(index)}
                                />
                            )}

                        <h3>{this.props.t('module.quiz.responses')}</h3>
                        <FieldArray
                            name={`${member}.quiz_responses`}
                            component={this.renderQuizResponse}
                        />
                    </Paper>
                );
            })}

            <RaisedButton
                style={styles.formButton}
                label={this.props.t('module.quiz.add_question')}
                type="button"
                primary={true}
                onClick={() => fields.push({
                    title: '',
                    display_order: fields.length,
                    quiz_responses: [],
                    key: 'new_' + Math.random().toString(16).substring(2, 8),
                    type: 'RADIO', // only one type available
                })}
            />
        </div>
    );

    onGeolocationTypeChange = (value) => {
        if (this.props.onGeolocationTypeChange && value) {
            this.props.onGeolocationTypeChange(value);
            this.props.change('latitude', null);
            this.props.change('longitude', null);
        }
    };

    render() {
        const {
            handleSubmit,
            pristine,
            reset,
            submitting,
        } = this.props;
        return (
            <form onSubmit={handleSubmit}>

                <Field
                    id="geolocation_type"
                    name="geolocation_type"
                    component={renderSelectField}
                    fullWidth={true}
                    initialValue={this.props.defaultGeolocationType}
                    label={this.props.t('module.quiz.geolocation_type')}
                    onCustomChange={this.onGeolocationTypeChange}
                >
                    {['none', 'gps'].map(type => (
                        <MenuItem
                            key={type}
                            value={type}
                            primaryText={this.props.t('module.quiz.geolocation_types.' + type)}
                        />
                    ))}
                </Field>

                <Field
                    id="latitude"
                    name="latitude"
                    component={renderNumberField}
                    label="Latitude"
                    onChange={this.onLatChanged}
                    fullWidth={true}
                    type="number"
                    parse={value => Number(value)}
                    disabled={this.props.geolocationType === 'none'}
                />
                <Field
                    id="longitude"
                    name="longitude"
                    component={renderNumberField}
                    label="Longitude"
                    onChange={this.onLngChanged}
                    fullWidth={true}
                    type="number"
                    parse={value => Number(value)}
                    disabled={this.props.geolocationType === 'none'}
                />
                <TextField
                    id="address"
                    hintText={this.props.t('module.quiz.address')}
                    floatingLabelText={this.props.t('module.quiz.address')}
                    onChange={this.onAddressChanged}
                    fullWidth={true}
                    value={this.props.address || ''}
                    disabled={this.props.geolocationType === 'none'}
                />
                <RaisedButton
                    label={this.props.t('module.quiz.find_address_on_map')}
                    disabled={submitting}
                    type="button"
                    onClick={this.onFindAddress}
                    primary={true}
                />
                <Field
                    id="name"
                    name="name"
                    component={renderTextField}
                    label={this.props.t('generic.name')}
                    fullWidth={true}
                />
                <h2>{this.props.t('module.quiz.questions')}</h2>
                <FieldArray
                    name="quiz_questions"
                    component={this.renderQuizQuestion}
                />
                <div style={styles.formButtons}>
                    <RaisedButton
                        style={styles.formButton}
                        label={this.props.t('generic.save')}
                        disabled={pristine || submitting}
                        type="submit"
                        primary={true}
                    />
                    {this.props.quiz == null &&
                        <RaisedButton
                            style={styles.formButton}
                            label={this.props.t('generic.save_and_create_new')}
                            disabled={pristine || submitting}
                            type="button"
                            primary={true}
                            onClick={() => {
                                // In case of success, handle submit returns undefined, otherwise it returns the list of errors
                                if (handleSubmit() === undefined) {
                                    reset();
                                    this.props.onFormReset();
                                }
                            }}
                        />
                    }
                    <RaisedButton
                        style={styles.formButton}
                        label={this.props.t('generic.reset')}
                        type="button"
                        disabled={pristine || submitting}
                        onClick={() => {
                            reset();
                            this.props.onFormReset();
                        }}
                    />
                </div>
            </form>
        );
    }
}

QuizForm = reduxForm({
    form: 'editQuiz',
    validate,
})(QuizForm);

const selector = formValueSelector('editQuiz');
QuizForm = connect(
    state => {
        const quizQuestions = selector(state, 'quiz_questions');
        const geolocationType = selector(state, 'geolocation_type');
        return {
            quizQuestions,
            geolocationType,
        };
    },
)(QuizForm);

export default withTranslation(QuizForm);



// WEBPACK FOOTER //
// src/components/quiz/QuizForm/QuizForm.js